/**
 * JMAP Mail Client
 *
 * Connects to Stalwart's JMAP endpoint for mail operations.
 * Uses the standard JMAP protocol (RFC 8620/8621).
 */

const JMAP_BASE = '/jmap'

export interface JmapSession {
  apiUrl: string
  downloadUrl: string
  uploadUrl: string
  eventSourceUrl: string
  accounts: Record<string, { name: string }>
  primaryAccounts: Record<string, string>
  capabilities: Record<string, unknown>
  username?: string
}

export interface Mailbox {
  id: string
  name: string
  parentId?: string
  role?: string
  totalEmails: number
  unreadEmails: number
  totalThreads: number
  unreadThreads: number
  myRights: {
    mayReadItems: boolean
    mayAddItems: boolean
    mayRemoveItems: boolean
    mayModifyItems: boolean
    mayCreateChild: boolean
    mayRename: boolean
    mayDelete: boolean
    maySubmit: boolean
  }
  sortOrder: number
  isSubscribed: boolean
}

export interface Email {
  id: string
  threadId: string
  mailboxIds: Record<string, boolean>
  keywords: Record<string, boolean>
  subject: string
  from: Array<{ name?: string; email: string }>
  to: Array<{ name?: string; email: string }>
  cc?: Array<{ name?: string; email: string }>
  bcc?: Array<{ name?: string; email: string }>
  receivedAt: string
  sentAt?: string
  preview: string
  textBody?: string
  htmlBody?: string
  attachments?: Array<{
    id: string
    partId: string
    type: string
    size: number
    name: string
  }>
  bodyValues?: Record<string, { value: string }>
}

export interface EmailListResponse {
  accountId: string
  filter?: unknown
  sort?: Array<{ property: string; isAscending: boolean }>
  state: string
  total: number
  position: number
  ids: string[]
}

export interface EmailGetResponse {
  accountId: string
  list: Email[]
  notFound: string[]
}

export class JmapMailClient {
  private auth: { username: string; password: string }
  private accountId: string | null = null

  constructor(auth: { username: string; password: string }) {
    this.auth = auth
  }

  private get authHeader(): string {
    return `Basic ${btoa(`${this.auth.username}:${this.auth.password}`)}`
  }

  async getSession(): Promise<JmapSession> {
    const res = await fetch('/session', {
      headers: { Authorization: this.authHeader },
    })
    if (!res.ok) {
      throw new Error(`Session error ${res.status}: ${await res.text()}`)
    }
    return res.json()
  }

  private async getAccountId(): Promise<string> {
    if (this.accountId) return this.accountId
    const session = await this.getSession()
    const mailAccount = session.primaryAccounts?.['urn:ietf:params:jmap:mail']
    if (!mailAccount) throw new Error('No mail account in session')
    this.accountId = mailAccount
    return this.accountId
  }

  async call(method: string, args: Record<string, unknown> = {}): Promise<unknown> {
    const accountId = await this.getAccountId()
    const callId = crypto.randomUUID()
    const res = await fetch(JMAP_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: this.authHeader,
      },
      body: JSON.stringify({
        using: ['urn:ietf:params:jmap:core', 'urn:ietf:params:jmap:mail'],
        methodCalls: [[method, { accountId, ...args }, callId]],
      }),
    })

    if (!res.ok) {
      const text = await res.text()
      throw new Error(`JMAP error ${res.status}: ${text}`)
    }

    const data = await res.json()
    const result = data.methodResponses?.[0]
    if (!result) throw new Error('Empty JMAP response')
    return result[1]
  }

  async getMailboxes(): Promise<Mailbox[]> {
    const result = await this.call('Mailbox/query', {})
    const ids = (result as EmailListResponse).ids || []
    if (ids.length === 0) return []

    const response = await this.call('Mailbox/get', { ids })
    return (response as { list: Mailbox[] }).list || []
  }

  async getEmails(
    mailboxId: string,
    options: {
      limit?: number
      position?: number
      sort?: string
      isAscending?: boolean
      filter?: Record<string, unknown>
      fetchTextBody?: boolean
      fetchHtmlBody?: boolean
    } = {}
  ): Promise<{ emails: Email[]; total: number; state: string }> {
    const {
      limit = 20,
      position = 0,
      sort = 'receivedAt',
      isAscending = false,
      filter = {},
      fetchTextBody = false,
      fetchHtmlBody = false,
    } = options

    const queryResult = await this.call('Email/query', {
      filter: {
        ...filter,
        inMailbox: mailboxId,
      },
      sort: [{ property: sort, isAscending }],
      position,
      limit,
    })

    const { ids, total, state } = queryResult as EmailListResponse
    if (!ids || ids.length === 0) return { emails: [], total: 0, state: '' }

    const properties = [
      'id', 'threadId', 'mailboxIds', 'keywords',
      'subject', 'from', 'to', 'receivedAt', 'preview',
    ]

    if (fetchTextBody) properties.push('textBody', 'bodyValues')
    if (fetchHtmlBody) properties.push('htmlBody', 'bodyValues')

    const getResult = await this.call('Email/get', {
      ids,
      properties,
      fetchTextBody,
      fetchHtmlBody,
    })

    return {
      emails: (getResult as EmailGetResponse).list || [],
      total: total || 0,
      state: state || '',
    }
  }

  async getEmail(id: string, options: {
    fetchTextBody?: boolean
    fetchHtmlBody?: boolean
    fetchAttachments?: boolean
  } = {}): Promise<Email | null> {
    const { fetchTextBody = true, fetchHtmlBody = true, fetchAttachments = true } = options

    const properties = [
      'id', 'threadId', 'mailboxIds', 'keywords',
      'subject', 'from', 'to', 'cc', 'bcc',
      'receivedAt', 'sentAt', 'preview',
    ]

    if (fetchTextBody) properties.push('textBody')
    if (fetchHtmlBody) properties.push('htmlBody')
    if (fetchAttachments) properties.push('attachments')

    properties.push('bodyValues')

    const result = await this.call('Email/get', {
      ids: [id],
      properties,
      fetchTextBody,
      fetchHtmlBody,
    })

    const list = (result as EmailGetResponse).list || []
    return list[0] || null
  }

  async setEmailFlags(
    ids: string[],
    flags: Record<string, boolean>
  ): Promise<void> {
    await this.call('Email/set', {
      update: Object.fromEntries(ids.map((id) => [id, { keywords: flags }])),
    })
  }

  async markAsRead(ids: string[]): Promise<void> {
    await this.setEmailFlags(ids, { '$seen': true })
  }

  async markAsUnread(ids: string[]): Promise<void> {
    await this.setEmailFlags(ids, { '$seen': false })
  }

  async moveToMailbox(ids: string[], mailboxId: string): Promise<void> {
    await this.call('Email/set', {
      update: Object.fromEntries(ids.map((id) => [id, { mailboxIds: { [mailboxId]: true } }])),
    })
  }

  async deleteEmails(ids: string[]): Promise<void> {
    await this.call('Email/set', { destroy: ids })
  }

  async submitEmail(params: {
    mailboxId?: string
    from: Array<{ name?: string; email: string }>
    to: Array<{ name?: string; email: string }>
    cc?: Array<{ name?: string; email: string }>
    bcc?: Array<{ name?: string; email: string }>
    subject: string
    textBody?: string
    htmlBody?: string
  }): Promise<string> {
    const identityResult = await this.call('Identity/get', {})
    const identities = (identityResult as { list?: Array<{ id: string; email: string }> }).list || []
    const identity = identities.find((i) => i.email === params.from[0]?.email) || identities[0]
    if (!identity) throw new Error('No identity found')

    const creationId = 'email_' + crypto.randomUUID().slice(0, 8)
    const createArgs: Record<string, unknown> = {
      mailboxIds: params.mailboxId ? { [params.mailboxId]: true } : { '$drafts': true },
      from: params.from,
      to: params.to,
      subject: params.subject,
    }
    if (params.cc) createArgs.cc = params.cc
    if (params.bcc) createArgs.bcc = params.bcc

    if (params.textBody) {
      const partId = 't1'
      createArgs.textBody = [{ partId, type: 'text/plain', disposition: 'inline' }]
      createArgs.bodyValues = { [partId]: { value: params.textBody } }
    }
    if (params.htmlBody) {
      const partId = 'h1'
      createArgs.htmlBody = [{ partId, type: 'text/html', disposition: 'inline' }]
      createArgs.bodyValues = { ...(createArgs.bodyValues as Record<string, unknown> || {}), [partId]: { value: params.htmlBody } }
    }

    const emailResult = await this.call('Email/set', {
      create: { [creationId]: createArgs },
    })
    const emailCreated = (emailResult as { created?: Record<string, { id: string }> }).created
    const emailId = emailCreated?.[creationId]?.id
    if (!emailId) throw new Error('Failed to create email')

    const toAddresses = params.to.map((t) => ({ email: t.email }))
    const submissionResult = await this.call('EmailSubmission/set', {
      create: {
        new1: {
          envelope: {
            mailFrom: { email: params.from[0]?.email || '' },
            rcptTo: toAddresses,
          },
          emailId,
          identityId: identity.id,
        },
      },
    })

    const submissionCreated = (submissionResult as { created?: Record<string, { id: string }> }).created
    return submissionCreated?.new1?.id || ''
  }

  async createMailbox(name: string, parentId?: string): Promise<string> {
    const result = await this.call('Mailbox/set', {
      create: {
        new1: {
          name,
          parentId,
        },
      },
    })

    const created = (result as { created?: Record<string, { id: string }> }).created
    return created?.new1?.id || ''
  }

  async renameMailbox(id: string, newName: string): Promise<void> {
    await this.call('Mailbox/set', {
      update: { [id]: { name: newName } },
    })
  }

  async deleteMailbox(id: string): Promise<void> {
    await this.call('Mailbox/set', { destroy: [id] })
  }

  async getMailboxCounts(): Promise<Record<string, { total: number; unread: number }>> {
    const mailboxes = await this.getMailboxes()
    const counts: Record<string, { total: number; unread: number }> = {}

    for (const mailbox of mailboxes) {
      counts[mailbox.id] = {
        total: mailbox.totalEmails,
        unread: mailbox.unreadEmails,
      }
    }

    return counts
  }
}

// Singleton instance
let mailClient: JmapMailClient | null = null

export function getMailClient(auth: { username: string; password: string }): JmapMailClient {
  if (!mailClient || mailClient['auth'].username !== auth.username) {
    mailClient = new JmapMailClient(auth)
  }
  return mailClient
}
